import { Input } from "@/components/ui/input";
import { Pencil } from "lucide-react";
import { useEffect, useRef, useState } from "react";

type InlineEditCellProps = {
  rowId: string;
  displayValue: string;
  onSave: (rowId: string, value: string) => void;
  ariaLabel: string;
  placeholder: string;
  title: string;
};

export function InlineEditCell({ rowId, displayValue, onSave, ariaLabel, placeholder, title }: InlineEditCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(displayValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isEditing) return;
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [isEditing]);

  const startEdit = () => {
    setDraft(displayValue);
    setIsEditing(true);
  };

  const commit = () => {
    onSave(rowId, draft);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            setIsEditing(false);
          }
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          }
        }}
        size={1}
        className="h-9 w-full max-w-xl min-w-0 px-2 shadow-none"
        autoComplete="off"
        aria-label={ariaLabel}
      />
    );
  }

  return (
    <button
      type="button"
      title={title}
      className="group text-foreground border-border/80 hover:bg-muted/50 hover:border-border focus-visible:ring-ring flex h-9 w-full max-w-xl min-w-0 cursor-pointer items-center gap-2 rounded-md border border-dashed px-2 text-left text-sm outline-none focus-visible:ring-2"
      onClick={startEdit}
    >
      <span className="min-w-0 flex-1">
        {displayValue.trim() ? (
          <span className="truncate">{displayValue}</span>
        ) : (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
      </span>
      <Pencil className="text-muted-foreground size-3.5 shrink-0 opacity-50 transition-opacity group-hover:opacity-100" aria-hidden />
    </button>
  );
}
